import { memo } from 'react';
import { TARGET_LEAGUES, type CalculatorMarket, type CalculatorMatchSelection } from '@nowscore/core';
import type { MatchDto } from '../api';
import { DATA_NOTE_LABELS, dataNoteLabel, formatKickoff } from '../lib/format';
import type { OddsView } from '../lib/odds';
import { BookmakerCompare } from './Bookmakers';
import { StatusBadge } from './Chrome';
import { LotteryMarketGrid } from './LotteryMarketGrid';

interface MatchCardProps {
  match: MatchDto;
  activeMarket: CalculatorMarket;
  oddsView: OddsView;
  calculatorSelection?: CalculatorMatchSelection | null;
  onCalculatorSelectionChange: (selection: CalculatorMatchSelection) => void;
  onOpen: (match: MatchDto) => void;
  onAnalyze: (match: MatchDto) => void;
}

function MatchCardInner({ match, activeMarket, oddsView, calculatorSelection, onCalculatorSelectionChange, onOpen, onAnalyze }: MatchCardProps) {
  const league = TARGET_LEAGUES.find((item) => item.code === match.leagueCode);
  const leagueName = match.leagueName ?? league?.name ?? match.leagueCode;
  const scheduled = match.status === 'scheduled';
  const hasBookmaker = (match.william?.length ?? 0) > 0 || (match.ladbrokes?.length ?? 0) > 0;
  const note = dataNoteLabel(match.lottery?.dataNote) ?? (league ? null : DATA_NOTE_LABELS.non_core_league);
  const selected = Boolean(calculatorSelection?.picks.length);
  return <article className={'match-card match-' + match.status + (selected ? ' selected' : '')}>
    <div className="match-topline">
      <span className={'lottery-pill' + (match.lottery ? '' : ' missing')}>{match.lottery?.number ?? '未配号'}</span>
      <span className="league-pill">{leagueName}</span>
      <StatusBadge match={match} />
      <time dateTime={match.kickoffTime}>{formatKickoff(match.kickoffTime)}</time>
      {selected && <span className="lottery-selected-pill">已选 {calculatorSelection?.picks.reduce((count, pick) => count + pick.selections.length, 0)} 项</span>}
    </div>
    <div className="card-main">
      <button type="button" className="match-summary-button" onClick={() => onOpen(match)} aria-label={'查看 ' + match.homeTeamName + ' vs ' + match.awayTeamName + ' 详情'}>
        <span className="match-main-row">
          <span className="team-name home">{match.homeTeamName}</span>
          <span className="score-stack">
            <span className={'score-box' + (scheduled ? ' pending' : '')}>{scheduled ? 'VS' : (match.score.home ?? '—') + ' : ' + (match.score.away ?? '—')}</span>
            {match.status !== 'scheduled' && <small className="score-sub">半场 {match.score.halfHome ?? '—'}:{match.score.halfAway ?? '—'} · 角 {match.live?.homeCorners ?? 0}:{match.live?.awayCorners ?? 0}{(match.live?.homeRed || match.live?.awayRed) ? ' · 红 ' + (match.live?.homeRed ?? 0) + ':' + (match.live?.awayRed ?? 0) : ''}</small>}
          </span>
          <span className="team-name away">{match.awayTeamName}</span>
        </span>
      </button>
      {match.lottery?.odds?.length ? <LotteryMarketGrid match={match} market={activeMarket} selection={calculatorSelection} onChange={onCalculatorSelectionChange} compact /> : <div className="lottery-block empty"><span className="lottery-block-title">官方竞彩</span><span className="lottery-empty">{note ?? '暂无数据'}</span></div>}
      {hasBookmaker ? <BookmakerCompare match={match} view={oddsView} compact /> : <span className="bookmaker-empty">{note ?? '威廉/立博数据待抓取'}</span>}
    </div>
    <div className="card-action-row">
      <button type="button" className="ai-analyze-button" onClick={() => onAnalyze(match)}><span aria-hidden="true">⚡</span> AI 分析</button>
      <button type="button" className="card-footer-button" onClick={() => onOpen(match)}>详情 · 历史 · 交锋 <span aria-hidden="true">→</span></button>
    </div>
  </article>;
}

export const MatchCard = memo(MatchCardInner);